import axios from "./axios";

const Offers = {
  /**
   * Check if current user has access to offers
   * @returns {Promise<{ok: boolean, data: {hasAccess: boolean, reason?: string}}>}
   */
  async checkAccess() {
    try {
      const res = await axios.get("/offers/check-access");
      return { ok: true, data: res.data };
    } catch (error) {
      return {
        ok: false,
        data: { hasAccess: false, reason: "ERROR" },
      };
    }
  },

  /**
   * Get all active offers
   * @returns {Promise<{ok: boolean, data: Array}>}
   */
  async getAll() {
    try {
      const res = await axios.get("/offers");
      return { ok: true, data: res.data };
    } catch (error) {
      return {
        ok: false,
        data: [],
        error: error.response?.data?.message || "Error fetching offers",
      };
    }
  },

  /**
   * Get a single offer by ID
   * @param {string} id - Offer ID
   * @returns {Promise<{ok: boolean, data: Object}>}
   */
  async getById(id) {
    try {
      const res = await axios.get(`/offers/${id}`);
      return { ok: true, data: res.data };
    } catch (error) {
      return {
        ok: false,
        data: null,
        error: error.response?.data?.message || "Error fetching offer",
      };
    }
  },

  /**
   * Get offers created by current user
   * @returns {Promise<{ok: boolean, data: Array}>}
   */
  async getMyOffers() {
    try {
      const res = await axios.get("/offers/my-offers");
      return { ok: true, data: res.data };
    } catch (error) {
      return {
        ok: false,
        data: [],
        error: error.response?.data?.message || "Error fetching my offers",
      };
    }
  },

  /**
   * Create a new offer
   * @param {Object} offerData - Offer data
   * @returns {Promise<{ok: boolean, data: Object}>}
   */
  async create(offerData) {
    try {
      const res = await axios.post("/offers", offerData);
      return { ok: true, data: res.data };
    } catch (error) {
      return {
        ok: false,
        data: null,
        error: error.response?.data?.message || "Error creating offer",
      };
    }
  },

  /**
   * Update an existing offer
   * @param {string} id - Offer ID
   * @param {Object} offerData - Updated offer data
   * @returns {Promise<{ok: boolean, data: Object}>}
   */
  async update(id, offerData) {
    try {
      const res = await axios.patch(`/offers/${id}`, offerData);
      return { ok: true, data: res.data };
    } catch (error) {
      return {
        ok: false,
        data: null,
        error: error.response?.data?.message || "Error updating offer",
      };
    }
  },

  /**
   * Delete an offer (soft delete)
   * @param {string} id - Offer ID
   * @returns {Promise<{ok: boolean}>}
   */
  async delete(id) {
    try {
      await axios.delete(`/offers/${id}`);
      return { ok: true };
    } catch (error) {
      return {
        ok: false,
        error: error.response?.data?.message || "Error deleting offer",
      };
    }
  },

  /**
   * Upload an image for an offer
   * @param {string} offerId - Offer ID
   * @param {File} file - The image file to upload
   * @returns {Promise<{ok: boolean, data?: Object}>}
   */
  async uploadImage(offerId, file) {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(`/offer-images/${offerId}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return { ok: true, data: res.data };
    } catch (error) {
      return {
        ok: false,
        error: error.response?.data?.message || "Error uploading image",
      };
    }
  },

  /**
   * Delete an offer's image
   * @param {string} offerId - Offer ID
   * @returns {Promise<{ok: boolean}>}
   */
  async deleteImage(offerId) {
    try {
      await axios.delete(`/offer-images/${offerId}`);
      return { ok: true };
    } catch (error) {
      return {
        ok: false,
        error: error.response?.data?.message || "Error deleting image",
      };
    }
  },

  /**
   * Get the URL for an offer's image
   * @param {string} offerId - Offer ID
   * @param {number} cacheBuster - Optional timestamp to force cache refresh
   * @returns {string} Full URL to the offer image
   */
  getImageUrl(offerId, cacheBuster = null) {
    // Use full backend URL from environment
    const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:3001/api';
    const url = `${apiBase}/offer-images/${offerId}`;
    return cacheBuster ? `${url}?t=${cacheBuster}` : url;
  },

  /**
   * Check if an offer has a custom image
   * @param {string} offerId - Offer ID
   * @returns {Promise<{ok: boolean, exists: boolean}>}
   */
  async checkImageExists(offerId) {
    try {
      const res = await axios.get(`/offer-images/${offerId}/exists`);
      return { ok: true, exists: res.data.exists, path: res.data.path };
    } catch (error) {
      return { ok: false, exists: false };
    }
  },
};

export default Offers;

